import { clsx } from 'clsx';
import type {
  AvailableSourceDocument,
  CrossDocumentPanelProps,
  CrossingDocumentType,
} from './MasterDocument.types';
import { Loading } from './components/loading';
import { useMasterDocumentTranslation } from './i18n';
import { useMasterDocumentFormatters } from './formatters';
import { inlineActionButton } from './inlineActions';

// ─── Badge de signo "ECD · +" / "DCD · −" (Story 105-5.02) ──
function SignBadge({ type }: { type: CrossingDocumentType }) {
  const isEcd = type === 'ECD';
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-bold',
        isEcd
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
          : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      )}
    >
      {type} · {isEcd ? '+' : '−'}
    </span>
  );
}

// ─── Fila de documento disponible ──────────────────────────
function AvailableRow({
  doc,
  selected,
  editable,
  disabled,
  onToggle,
  formatCurrency,
  lineCountText,
}: {
  doc: AvailableSourceDocument;
  selected: boolean;
  editable: boolean;
  disabled: boolean;
  onToggle: (id: string, selected: boolean) => void;
  formatCurrency: (value: number) => string;
  /** Devuelve la frase completa ya pluralizada, p. ej. "3 líneas". */
  lineCountText: (count: number) => string;
}) {
  return (
    <tr
      className={clsx(
        'border-t border-border-primary dark:border-dark-border-primary transition-colors',
        selected && 'bg-primary-custom-50 dark:bg-dark-bg-custom/40',
      )}
    >
      {editable && (
        <td className="px-2 py-2 align-middle">
          <input
            type="checkbox"
            checked={selected}
            disabled={disabled}
            onChange={(e) => onToggle(doc.id, e.target.checked)}
            aria-label={`Seleccionar ${doc.classLabel} ${doc.number}`}
            className="w-4 h-4 rounded border-border-primary dark:border-dark-border-primary accent-primary-custom-600 cursor-pointer disabled:cursor-not-allowed"
          />
        </td>
      )}
      <td className="px-2 py-2 align-middle">
        <div className="flex items-center gap-2">
          <SignBadge type={doc.type} />
          <span className="text-sm font-medium text-content-primary dark:text-dark-content-primary">
            {doc.number}
          </span>
        </div>
        <span className="block text-xs text-content-secondary dark:text-dark-content-secondary">
          {doc.classLabel}
          {doc.date ? ` · ${doc.date}` : ''}
          {doc.lineCount != null ? ` · ${lineCountText(doc.lineCount)}` : ''}
        </span>
      </td>
      <td className="px-2 py-2 align-middle text-right text-sm font-mono text-content-primary dark:text-dark-content-primary whitespace-nowrap">
        {doc.crossingSign < 0 ? '−' : '+'}
        {formatCurrency(doc.netTotal)}
      </td>
    </tr>
  );
}

/**
 * CrossDocumentPanel — panel de cruce WHOLE-DOCUMENT (Épica 105-5).
 *
 * Selección de documentos ENTEROS (ECD/DCD) con badge de signo, subtotal corrido
 * `Σ(ECDs.net) − Σ(DCDs.net)` reactivo, acción "Cruzar" y, sobre los ya cruzados,
 * "Desvincular" por documento. En modo no editable se presenta como histórico
 * read-only (sin checkboxes ni acciones). Alimentado por {@link useCrossDocuments}.
 */
export function CrossDocumentPanel({
  available,
  selectedIds,
  subtotal,
  loading = false,
  busy = false,
  editable = false,
  error,
  onToggleSelect,
  onCross,
  onRefresh,
  formatCurrency: formatCurrencyProp,
  messages,
  className,
}: CrossDocumentPanelProps) {
  const { t } = useMasterDocumentTranslation();
  const { formatCurrency: formatCurrencyDefault } = useMasterDocumentFormatters();
  const formatCurrency = formatCurrencyProp ?? formatCurrencyDefault;
  const selected = new Set(selectedIds);
  const title = messages?.title ?? t('master_document.crossing.panelTitle');
  const emptyAvailable = messages?.emptyAvailable ?? t('master_document.crossing.emptyAvailable');
  const subtotalLabel = messages?.subtotalLabel ?? t('master_document.crossing.subtotalLabel');
  const crossButton = messages?.crossButton ?? t('master_document.crossing.crossButton');
  // El override del template sigue siendo una etiqueta suelta (contrato histórico),
  // así que en ese camino se concatena como antes. Sin override, la clave con
  // plural produce la frase completa y el orden lo decide la traducción.
  const lineCountText = (count: number): string =>
    messages?.lineCountLabel
      ? `${count} ${messages.lineCountLabel}`
      : t('master_document.crossing.lineCount', { count });

  return (
    <div
      className={clsx(
        'bg-bg-primary dark:bg-dark-bg-primary',
        'border border-border-primary dark:border-dark-border-primary',
        'rounded-xl p-3',
        className,
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-content-secondary dark:text-dark-content-secondary uppercase tracking-wide">
          {title}
        </span>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || busy}
            className={inlineActionButton}
          >
            {t('master_document.chrome.refresh')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-2 px-2.5 py-2 rounded-md text-xs bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800">
          {error}
        </div>
      )}

      {loading ? (
        <Loading size="md" label={t('master_document.crossing.loading')} />
      ) : available.length === 0 ? (
        <p className="text-sm text-content-secondary dark:text-dark-content-secondary py-2">{emptyAvailable}</p>
      ) : (
        <table className="w-full text-left">
          <tbody>
            {available.map((doc) => (
              <AvailableRow
                key={doc.id}
                doc={doc}
                selected={selected.has(doc.id)}
                editable={editable}
                disabled={busy}
                onToggle={onToggleSelect}
                formatCurrency={formatCurrency}
                lineCountText={lineCountText}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Subtotal corrido + acción Cruzar (solo editable) */}
      {editable && available.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-primary dark:border-dark-border-primary flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-xs text-content-secondary dark:text-dark-content-secondary">{subtotalLabel}</span>
            <span className="text-base font-bold font-mono text-content-primary dark:text-dark-content-primary">
              {formatCurrency(subtotal)}
            </span>
          </div>
          <button
            type="button"
            onClick={onCross}
            disabled={busy || selectedIds.length === 0}
            className={clsx(
              'px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer',
              'bg-primary-custom-600 text-white hover:bg-primary-custom-700',
              'dark:bg-dark-bg-custom dark:text-dark-content-custom dark:hover:bg-dark-border-custom',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {busy ? t('master_document.crossing.crossing') : `${crossButton}${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
          </button>
        </div>
      )}
    </div>
  );
}
